import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { ArrowLeft, Plus, Upload, Download, Trash2, CheckCircle, XCircle, LogOut, Link as LinkIcon, Clock, RefreshCw, Users, UserPlus, Check, X, MessageSquare, Send, Edit, Replace, Bell, BellOff, Settings, Loader2, Pencil, FileSignature, Eye } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectDetail = ({ user, onLogout }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newDocumentName, setNewDocumentName] = useState('');
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const [checkingAll, setCheckingAll] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [customDocName, setCustomDocName] = useState('');
  const [backupLoading, setBackupLoading] = useState(false);
  // R2 Relink state
  const [relinkLoading, setRelinkLoading] = useState(false);
  const [relinkDialogOpen, setRelinkDialogOpen] = useState(false);
  const [relinkResult, setRelinkResult] = useState(null);
  const [relinkStep, setRelinkStep] = useState('preview');
  const [signatureUploading, setSignatureUploading] = useState(null);
  const [aiCheckDialogOpen, setAiCheckDialogOpen] = useState(false);
  
  // Edit project states
  const [editProjectDialogOpen, setEditProjectDialogOpen] = useState(false);
  const [editProjectName, setEditProjectName] = useState('');
  const [editClientName, setEditClientName] = useState('');
  const [editDeadline, setEditDeadline] = useState('');
  const [editTemplateId, setEditTemplateId] = useState('');
  const [availableTemplates, setAvailableTemplates] = useState([]);
  
  // Document note states
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editingNoteDoc, setEditingNoteDoc] = useState(null);
  const [noteText, setNoteText] = useState('');
  
  // Collaborator states
  const [collaborators, setCollaborators] = useState([]);
  const [collaboratorDialogOpen, setCollaboratorDialogOpen] = useState(false);
  const [newCollaborator, setNewCollaborator] = useState({ 
    email: '', 
    role: 'client', 
    role_name: '',
    full_name: '',
    phone: ''
  });
  
  // Review states
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewingDocument, setReviewingDocument] = useState(null);
  const [reviewData, setReviewData] = useState({ status: 'approved', comment: '' });
  
  // Assignment states
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningDocument, setAssigningDocument] = useState(null);
  const [selectedCollaborators, setSelectedCollaborators] = useState([]);
  
  // Reminders state
  const [sendingReminders, setSendingReminders] = useState(false);
  
  // Edit collaborator state
  const [editCollaboratorDialogOpen, setEditCollaboratorDialogOpen] = useState(false);
  const [editingCollaborator, setEditingCollaborator] = useState(null);
  
  // Notification settings state
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    notification_enabled: true,
    notification_interval_days: 3,
    advisor_escalation_enabled: false,
    advisor_escalation_threshold: 3
  });

  // Expiry tracking states
  const [expiryDialogOpen, setExpiryDialogOpen] = useState(false);
  const [expiryEditingDoc, setExpiryEditingDoc] = useState(null);
  const [expiryIssueDate, setExpiryIssueDate] = useState('');
  const [expiryExpiryDate, setExpiryExpiryDate] = useState('');
  const [expirySaving, setExpirySaving] = useState(false);

  useEffect(() => {
    fetchProjectDetails();
    fetchCollaborators();
    
    // Auto-refresh every 5 seconds to show new uploads
    const interval = setInterval(() => {
      fetchProjectDetails();
    }, 3000);
    
    return () => clearInterval(interval);
  }, [projectId]);

  const fetchProjectDetails = async () => {
    try {
      const [projectRes, docsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/documents`),
      ]);
      setProject(projectRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης έργου');
    }
  };

  const fetchCollaborators = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/collaborators`);
      setCollaborators(response.data);
    } catch (error) {
      console.error('Error fetching collaborators:', error);
    }
  };

  const handleAddCollaborator = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/projects/${projectId}/collaborators`, newCollaborator);
      toast.success('Ο συνεργάτης προστέθηκε');
      setNewCollaborator({ email: '', role: 'client', role_name: '' });
      setCollaboratorDialogOpen(false);
      fetchCollaborators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα προσθήκης συνεργάτη');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCollaborator = async (collaboratorId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/collaborators/${collaboratorId}`);
      toast.success('Ο συνεργάτης αφαιρέθηκε');
      fetchCollaborators();
    } catch (error) {
      toast.error('Σφάλμα αφαίρεσης συνεργάτη');
    }
  };

  const openEditCollaborator = (collab) => {
    setEditingCollaborator({ ...collab });
    setEditCollaboratorDialogOpen(true);
  };

  const handleUpdateCollaborator = async (e) => {
    e.preventDefault();
    if (!editingCollaborator) return;
    setLoading(true);
    try {
      await axios.put(
        `${API}/projects/${projectId}/collaborators/${editingCollaborator.collaborator_id}`,
        {
          email: editingCollaborator.email,
          full_name: editingCollaborator.full_name,
          phone: editingCollaborator.phone,
          role: editingCollaborator.role,
          role_name: editingCollaborator.role_name
        }
      );
      toast.success('Ο συνεργάτης ενημερώθηκε');
      setEditCollaboratorDialogOpen(false);
      setEditingCollaborator(null);
      fetchCollaborators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα ενημέρωσης συνεργάτη');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/application-templates?user_id=${user.user_id}`);
      setAvailableTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleReviewDocument = async (e) => {
    e.preventDefault();
    if (!reviewingDocument) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/documents/${reviewingDocument.document_id}/review`, reviewData);
      toast.success(reviewData.status === 'approved' ? 'Το έγγραφο εγκρίθηκε' : 'Το έγγραφο απορρίφθηκε');
      setReviewDialogOpen(false);
      setReviewingDocument(null);
      setReviewData({ status: 'approved', comment: '' });
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα αξιολόγησης');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignDocument = async () => {
    if (!assigningDocument || selectedCollaborators.length === 0) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/documents/${assigningDocument.document_id}/assign`, selectedCollaborators);
      toast.success('Το έγγραφο ανατέθηκε');
      setAssignDialogOpen(false);
      setAssigningDocument(null);
      setSelectedCollaborators([]);
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα ανάθεσης');
    } finally {
      setLoading(false);
    }
  };

  const getRoleDisplayName = (role, roleName) => {
    if (role === 'accountant') return 'Λογιστής';
    if (role === 'client') return 'Πελάτης';
    if (role === 'other' && roleName) return roleName;
    return role;
  };

  const handleSendReminders = async () => {
    if (collaborators.length === 0) {
      toast.error('Προσθέστε πρώτα συνεργάτες');
      return;
    }
    
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/projects/${projectId}/send-reminders`);
      toast.success(`${response.data.message} (${response.data.pending_count} εκκρεμή, ${response.data.rejected_count} λάθος)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα αποστολής');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleReplaceFile = async (documentId, files) => {
    if (!files || files.length === 0) return;
    
    setUploadingDocId(documentId);
    const formData = new FormData();
    // Append all files
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      await axios.post(`${API}/documents/${documentId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Ανέβηκαν ${files.length} αρχεία`);
      fetchProjectDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα ανεβάσματος αρχείων');
    } finally {
      setUploadingDocId(null);
    }
  };

  const handleSaveNotificationSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/projects/${projectId}`, notificationSettings);
      toast.success('Οι ρυθμίσεις ειδοποιήσεων αποθηκεύτηκαν');
      setNotificationDialogOpen(false);
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης ρυθμίσεων');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDocument = async (documentName = null) => {
    const nameToAdd = documentName || newDocumentName;
    if (!nameToAdd) return;
    
    setLoading(true);

    try {
      await axios.post(`${API}/projects/${projectId}/documents`, {
        document_name: nameToAdd,
      });
      toast.success('Το δικαιολογητικό προστέθηκε');
      setNewDocumentName('');
      setSearchResults([]);
      setDialogOpen(false);
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα προσθήκης δικαιολογητικού');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (documentId, files) => {
    if (!files || files.length === 0) return;

    setUploadingDocId(documentId);
    const formData = new FormData();
    // Append all files
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      await axios.post(`${API}/documents/${documentId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Ανέβηκαν ${files.length} αρχεία επιτυχώς`);
      fetchProjectDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα ανεβάσματος αρχείων');
    } finally {
      setUploadingDocId(null);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchProjectDetails();
      toast.success('Η λίστα ανανεώθηκε!');
    } catch (error) {
      toast.error('Σφάλμα ανανέωσης');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSearchTemplates = async (query) => {
    setNewDocumentName(query);
    if (query.length === 0) {
      // Fetch initial templates when empty
      try {
        const response = await axios.get(`${API}/document-templates/search?q=`);
        setSearchResults(response.data);
      } catch (error) {
        setSearchResults([]);
      }
    } else if (query.length >= 1) {
      try {
        const response = await axios.get(`${API}/document-templates/search?q=${encodeURIComponent(query)}`);
        setSearchResults(response.data);
      } catch (error) {
        setSearchResults([]);
      }
    }
  };

  const handleSelectTemplate = (templateName) => {
    setNewDocumentName(templateName);
    setSearchResults([]);
  };

  const handleAddCustomDocument = async (e) => {
    e.preventDefault();
    if (!customDocName.trim()) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/projects/${projectId}/documents`, {
        document_name: customDocName,
      });
      toast.success('Το δικαιολογητικό προστέθηκε');
      setCustomDocName('');
      setShowOtherInput(false);
      setDialogOpen(false);
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα προσθήκης δικαιολογητικού');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckAll = async () => {
    if (project.checks_used >= project.max_checks) {
      toast.error(`Δεν έχετε άλλους ελέγχους διαθέσιμους. Έχετε χρησιμοποιήσει ${project.max_checks}/${project.max_checks} ελέγχους.`);
      return;
    }

    // Check for documents with NEW files (status='uploaded' only)
    const newDocsWithFiles = documents.filter(doc => {
      const paths = doc.file_paths?.length > 0 ? doc.file_paths : (doc.file_path ? [doc.file_path] : []);
      return paths.length > 0 && doc.status === 'uploaded';
    });
    
    if (newDocsWithFiles.length === 0) {
      // Check if all docs are already checked
      const checkedDocs = documents.filter(doc => doc.status === 'checked');
      if (checkedDocs.length > 0) {
        toast.info('Όλα τα δικαιολογητικά έχουν ήδη ελεγχθεί. Ανεβάστε νέα αρχεία για να τα ελέγξετε.');
      } else {
        toast.error('Δεν υπάρχουν αρχεία προς έλεγχο. Ανεβάστε αρχεία στα δικαιολογητικά.');
      }
      return;
    }

    setCheckingAll(true);
    setAiCheckDialogOpen(true);
    try {
      // Use the batch check endpoint - counts as 1 check operation
      const response = await axios.post(`${API}/projects/${projectId}/check-all?user_id=${user.user_id}`);
      
      toast.success(`Ελέγχθηκαν ${response.data.documents_checked} νέα δικαιολογητικά! Ελέγχοι: ${project.checks_used + 1}/${project.max_checks}`);
      fetchProjectDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα ελέγχου');
    } finally {
      setCheckingAll(false);
      setAiCheckDialogOpen(false);
    }
  };

  // Edit project name and client
  const handleEditProject = async () => {
    try {
      await axios.put(`${API}/projects/${projectId}`, {
        project_title: editProjectName,
        client_name: editClientName,
        deadline: editDeadline || null,
        template_id: editTemplateId || null
      });
      toast.success('Τα στοιχεία του έργου ενημερώθηκαν');
      setEditProjectDialogOpen(false);
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης έργου');
    }
  };

  const openEditProjectDialog = () => {
    setEditProjectName(project?.project_title || project?.project_name || '');
    setEditClientName(project?.client_name || '');
    setEditDeadline(project?.deadline || '');
    setEditTemplateId(project?.template_id || '');
    fetchTemplates();
    setEditProjectDialogOpen(true);
  };

  // Document note functions
  const openNoteDialog = (doc) => {
    setEditingNoteDoc(doc);
    setNoteText(doc.note || '');
    setNoteDialogOpen(true);
  };

  const handleSaveNote = async () => {
    try {
      await axios.put(`${API}/documents/${editingNoteDoc.document_id}/note`, {
        note: noteText || null
      });
      toast.success('Η σημείωση αποθηκεύτηκε');
      setNoteDialogOpen(false);
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης σημείωσης');
    }
  };

  const handleDeleteNote = async () => {
    try {
      await axios.delete(`${API}/documents/${editingNoteDoc.document_id}/note`);
      toast.success('Η σημείωση διαγράφηκε');
      setNoteDialogOpen(false);
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής σημείωσης');
    }
  };

  // Signature functions
  const handleToggleSignature = async (doc) => {
    try {
      if (doc.signature_required) {
        await axios.post(`${API}/documents/${doc.document_id}/signature/disable`);
        toast.success('Η υπογραφή απενεργοποιήθηκε');
      } else {
        await axios.post(`${API}/documents/${doc.document_id}/signature/enable`);
        toast.success('Η υπογραφή ενεργοποιήθηκε');
      }
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα αλλαγής κατάστασης υπογραφής');
    }
  };

  const handleUploadForSigning = async (docId, file) => {
    if (!file) return;
    setSignatureUploading(docId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/documents/${docId}/signature/upload-for-signing`, formData);
      toast.success('Το αρχείο για υπογραφή ανέβηκε');
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα ανεβάσματος αρχείου');
    } finally {
      setSignatureUploading(null);
    }
  };

  const handleDownloadForSigning = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.document_id}/signature/download-for-signing`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `για_υπογραφή_${doc.document_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Σφάλμα λήψης αρχείου');
    }
  };

  const handleDownloadSigned = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.document_id}/signature/download-signed`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `υπογεγραμμένο_${doc.document_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Σφάλμα λήψης αρχείου');
    }
  };

  const handleDeleteSignatureFile = async (docId, fileType) => {
    try {
      await axios.delete(`${API}/documents/${docId}/signature/file?file_type=${fileType}`);
      toast.success('Το αρχείο διαγράφηκε');
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής αρχείου');
    }
  };

  const handleR2Relink = async () => {
    setRelinkLoading(true);
    setRelinkResult(null);
    setRelinkStep('preview');
    setRelinkDialogOpen(true);
    try {
      const diagResp = await axios.get(`${API}/admin/diagnose-project-files?project_id=${projectId}`);
      const relinkResp = await axios.post(`${API}/admin/relink-r2-files?project_id=${projectId}&dry_run=true`);
      setRelinkResult({ ...relinkResp.data, diagnostic: diagResp.data });
    } catch (error) {
      console.error('R2 relink error:', error);
      toast.error(error.response?.data?.detail || 'Σφάλμα συγχρονισμού με R2');
      setRelinkDialogOpen(false);
    } finally {
      setRelinkLoading(false);
    }
  };

  const handleR2RelinkApply = async () => {
    setRelinkLoading(true);
    try {
      const resp = await axios.post(`${API}/admin/relink-r2-files?project_id=${projectId}&dry_run=false`);
      setRelinkResult(resp.data);
      setRelinkStep('applied');
      toast.success(`Συγχρονίστηκαν ${resp.data.documents_relinked} έγγραφα από το R2`);
      await fetchProjectDetails();
    } catch (error) {
      console.error('R2 relink apply error:', error);
      toast.error(error.response?.data?.detail || 'Σφάλμα εφαρμογής συγχρονισμού');
    } finally {
      setRelinkLoading(false);
    }
  };

  const handleBackupProject = async () => {
    setBackupLoading(true);
    try {
      // Check files with retries
      let check = null;
      let retries = 3;
      
      for (let attempt = 1; attempt <= retries; attempt++) {
        const checkResponse = await axios.get(`${API}/projects/${projectId}/backup-check`);
        check = checkResponse.data;
        
        if (check.files_missing === 0) {
          break; // All files found
        }
        
        if (attempt < retries) {
          // Wait and retry
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      if (check.files_found === 0) {
        toast.error('Δεν υπάρχουν αρχεία για backup');
        setBackupLoading(false);
        return;
      }
      
      if (check.files_missing > 0) {
        // Show which documents have missing files
        const missingDocs = check.details
          .filter(d => d.files_missing.length > 0)
          .map(d => d.document_name)
          .join(', ');
        
        toast.error(
          `Σφάλμα: Λείπουν ${check.files_missing} αρχεία από: ${missingDocs}. Παρακαλώ ανεβάστε τα ξανά.`,
          { duration: 8000 }
        );
        setBackupLoading(false);
        return;
      }
      
      // All files exist - proceed with backup
      const response = await axios.get(`${API}/projects/${projectId}/backup`, {
        responseType: 'blob',
      });

      const totalFiles = response.headers['x-total-files'] || check.files_found;
      const totalSize = response.headers['x-total-size'] || 'unknown';
      const sizeMB = totalSize !== 'unknown' ? (totalSize / (1024 * 1024)).toFixed(2) : 'unknown';

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.setAttribute('download', `backup_project_${timestamp}.zip`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Backup ολοκληρώθηκε! ${totalFiles} αρχεία (${sizeMB} MB)`);
    } catch (error) {
      toast.error('Σφάλμα δημιουργίας backup');
      console.error('Backup error:', error);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDownload = async (documentId, fileIndex = 0, fileName = null) => {
    try {
      const response = await axios.get(`${API}/documents/${documentId}/download?file_index=${fileIndex}`, {
        responseType: 'blob',
      });
      // Extract filename from Content-Disposition header if not provided
      let downloadName = fileName;
      if (!downloadName) {
        const disposition = response.headers['content-disposition'];
        if (disposition) {
          const utf8Match = disposition.match(/filename\*=UTF-8''(.+)/);
          if (utf8Match) downloadName = decodeURIComponent(utf8Match[1]);
        }
      }
      if (!downloadName) downloadName = 'document.pdf';
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Σφάλμα λήψης αρχείου');
    }
  };

  const handleDeleteFile = async (documentId, fileIndex = -1) => {
    try {
      await axios.delete(`${API}/documents/${documentId}/file?file_index=${fileIndex}`);
      toast.success('Το αρχείο διαγράφηκε');
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής αρχείου');
    }
  };

  // Mark document as reviewed by owner (removes green highlight)
  const handleMarkReviewed = async (documentId) => {
    try {
      await axios.post(`${API}/documents/${documentId}/mark-reviewed`);
      toast.success('Το έγγραφο σημειώθηκε ως ελεγμένο');
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα κατά τη σημείωση');
    }
  };

  // Helper to get file name from path
  const getFileName = (filePath) => {
    if (!filePath) return 'document.pdf';
    // R2 paths: r2://projects/.../uuid_filename.ext
    if (filePath.startsWith('r2://')) {
      const parts = filePath.split('/');
      const fullName = parts[parts.length - 1];
      // Format: 8char-uuid_originalname - split at first underscore
      const idx = fullName.indexOf('_');
      return idx > 0 ? fullName.substring(idx + 1) : fullName;
    }
    // GridFS paths with filename: gridfs:objectid:gen_docid_fileid_filename
    if (filePath.startsWith('gridfs:') && filePath.split(':').length >= 3) {
      const parts = filePath.split(':');
      const rawName = parts.slice(2).join(':');
      // Format: gen_docid_fileid_originalname - remove first 3 parts
      const nameParts = rawName.split('_');
      return nameParts.length > 3 ? nameParts.slice(3).join('_') : rawName;
    }
    // GridFS paths without filename: gridfs://objectid
    if (filePath.startsWith('gridfs:')) {
      return null; // Will use Content-Disposition from backend
    }
    // Local file paths: /path/uuid_filename.ext - split at first underscore
    const parts = filePath.split('/');
    const fullName = parts[parts.length - 1];
    const idx = fullName.indexOf('_');
    return idx > 0 ? fullName.substring(idx + 1) : fullName;
  };

  // Helper to get file_paths from document (with backward compatibility)
  const getFilePaths = (doc) => {
    if (doc.file_paths && doc.file_paths.length > 0) {
      return doc.file_paths;
    }
    if (doc.file_path) {
      return [doc.file_path];
    }
    return [];
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      await axios.delete(`${API}/documents/${documentId}`);
      toast.success('Το δικαιολογητικό διαγράφηκε');
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής δικαιολογητικού');
    }
  };

  const copyShareLink = () => {
    const shareUrl = `${window.location.origin}/upload/${project.share_id}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Ο σύνδεσμος αντιγράφηκε');
  };

  const copyProgressLink = () => {
    const progressShareId = project.progress_share_id || project.project_id;
    const progressUrl = `${window.location.origin}/progress/app/${progressShareId}`;
    navigator.clipboard.writeText(progressUrl);
    toast.success('Ο σύνδεσμος προόδου αντιγράφηκε');
  };

  // Expiry tracking functions
  const openExpiryDialog = (doc) => {
    setExpiryEditingDoc(doc);
    setExpiryIssueDate(doc.issue_date || '');
    setExpiryExpiryDate(doc.expiry_date || '');
    setExpiryDialogOpen(true);
  };

  const handleSaveExpiry = async () => {
    if (!expiryEditingDoc) return;
    setExpirySaving(true);
    try {
      await axios.put(`${API}/documents/${expiryEditingDoc.document_id}/expiry`, {
        issue_date: expiryIssueDate || null,
        expiry_date: expiryExpiryDate || null,
        expiry_tracking: !!expiryExpiryDate
      });
      toast.success('Οι ημερομηνίες αποθηκεύτηκαν');
      setExpiryDialogOpen(false);
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης ημερομηνιών');
    } finally {
      setExpirySaving(false);
    }
  };

  const handleClearExpiry = async () => {
    if (!expiryEditingDoc) return;
    setExpirySaving(true);
    try {
      await axios.put(`${API}/documents/${expiryEditingDoc.document_id}/expiry`, {
        issue_date: null,
        expiry_date: null,
        expiry_tracking: false
      });
      toast.success('Οι ημερομηνίες διαγράφηκαν');
      setExpiryDialogOpen(false);
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής ημερομηνιών');
    } finally {
      setExpirySaving(false);
    }
  };

  const getExpiryInfo = (doc) => {
    if (!doc.expiry_date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(doc.expiry_date);
    expiry.setHours(0, 0, 0, 0);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return { daysLeft: diffDays, expired: diffDays < 0, expiringSoon: diffDays >= 0 && diffDays <= 30 };
  };

  const getStatusBadge = (doc) => {
    if (doc.ai_status === 'PASS') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium">
          <CheckCircle className="h-3 w-3 mr-1" />
          PASS
        </span>
      );
    } else if (doc.ai_status === 'FAIL') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-md bg-rose-50 text-rose-700 text-xs font-medium">
          <XCircle className="h-3 w-3 mr-1" />
          FAIL
        </span>
      );
    } else if (doc.status === 'uploaded') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
          Ανεβασμένο
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-md bg-amber-50 text-amber-700 text-xs font-medium">
          <Clock className="h-3 w-3 mr-1" />
          Εκκρεμεί
        </span>
      );
    }
  };

  if (!project) {
    return <div className="flex items-center justify-center h-screen">Φόρτωση...</div>;
  }

  return (
    <div className="flex h-screen bg-slate-50">
      <div className="sidebar">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900" data-testid="project-detail-title">ESPA Manager</h2>
          <p className="text-sm text-slate-600 mt-1">{user.email}</p>
        </div>

        <nav className="p-4">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="w-full justify-start mb-2"
            data-testid="back-to-dashboard-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Πίσω στα Έργα
          </Button>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <Button
            onClick={onLogout}
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-slate-900"
            data-testid="logout-button"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Αποσύνδεση
          </Button>
        </div>
      </div>

      <div className="main-content">
        <div className="p-8">
          <div className="mb-8">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="project-title">
                {project.project_title}
              </h1>
              <Button
                variant="ghost"
                size="sm"
                onClick={openEditProjectDialog}
                className="text-slate-500 hover:text-slate-700"
                data-testid="edit-project-name-button"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-slate-600 mt-1">Πελάτης: {project.client_name}</p>
            {project.deadline && (
              <p className="text-slate-600 mt-1 flex items-center gap-2">
                <Clock className="h-4 w-4 text-orange-500" />
                Προθεσμία: {new Date(project.deadline).toLocaleDateString('el-GR')}
              </p>
            )}

            <div className="flex items-center gap-4 mt-4">
              <Button
                onClick={copyShareLink}
                variant="outline"
                className="flex items-center gap-2"
                data-testid="copy-share-link-button"
              >
                <LinkIcon className="h-4 w-4" />
                Αντιγραφή Συνδέσμου Upload
              </Button>

              <Button
                onClick={copyProgressLink}
                variant="outline"
                className="flex items-center gap-2 border-cyan-600 text-cyan-600 hover:bg-cyan-50"
                data-testid="copy-progress-link-button"
              >
                <Eye className="h-4 w-4" />
                Κοινοποίηση Προόδου
              </Button>

              <Button
                onClick={handleBackupProject}
                disabled={backupLoading}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
                data-testid="backup-project-button"
              >
                <Download className={`h-4 w-4 mr-2 ${backupLoading ? 'animate-bounce' : ''}`} />
                {backupLoading ? 'Backup...' : 'Backup Έργου'}
              </Button>

              <Button
                onClick={handleR2Relink}
                disabled={relinkLoading}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
                data-testid="r2-relink-btn"
                title="Επανασύνδεση αρχείων από το Cloudflare R2"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${relinkLoading ? 'animate-spin' : ''}`} />
                Συγχρονισμός με R2
              </Button>

              <Button
                onClick={() => {
                  setNotificationSettings({
                    notification_enabled: project.notification_enabled ?? true,
                    notification_interval_days: project.notification_interval_days ?? 3,
                    advisor_escalation_enabled: project.advisor_escalation_enabled ?? false,
                    advisor_escalation_threshold: project.advisor_escalation_threshold ?? 3
                  });
                  setNotificationDialogOpen(true);
                }}
                variant="outline"
                className={`flex items-center gap-2 ${project.notification_enabled ? 'border-emerald-600 text-emerald-600' : 'border-slate-400 text-slate-400'}`}
                data-testid="notification-settings-button"
              >
                {project.notification_enabled ? (
                  <Bell className="h-4 w-4" />
                ) : (
                  <BellOff className="h-4 w-4" />
                )}
                Ειδοποιήσεις
              </Button>

              <div className="text-sm text-slate-600">
                Έλεγχοι: <span className="font-medium text-slate-900">{project.checks_used}/{project.max_checks}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900" data-testid="documents-heading">Δικαιολογητικά</h2>

            <div className="flex gap-2">
              <Button
                onClick={handleRefresh}
                disabled={refreshing}
                variant="outline"
                className="border-slate-300"
                data-testid="refresh-button"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Ανανέωση...' : 'Ανανέωση Δικαιολογητικών'}
              </Button>

              {user.ai_check_enabled !== false && (
              <Button
                onClick={handleCheckAll}
                disabled={checkingAll || project.checks_used >= project.max_checks}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="check-all-button"
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                {checkingAll ? 'Έλεγχος...' : `Έλεγχος Όλων (${project.checks_used}/${project.max_checks})`}
              </Button>
              )}

              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (open) {
                  handleSearchTemplates('');
                  setShowOtherInput(false);
                  setCustomDocName('');
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-document-button">
                    <Plus className="mr-2 h-4 w-4" />
                    Προσθήκη Δικαιολογητικού
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="add-document-dialog" aria-describedby="add-document-description">
                  <DialogHeader>
                    <DialogTitle>Νέο Δικαιολογητικό</DialogTitle>
                  </DialogHeader>
                  <p id="add-document-description" className="text-sm text-slate-600 mb-4">
                    Αναζητήστε από τη λίστα ή προσθέστε άλλο έγγραφο
                  </p>
                  
                  {!showOtherInput ? (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="document-name">Αναζήτηση Δικαιολογητικού</Label>
                        <Input
                          id="document-name"
                          value={newDocumentName}
                          onChange={(e) => handleSearchTemplates(e.target.value)}
                          placeholder="Πληκτρολογήστε για αναζήτηση..."
                          data-testid="document-name-input"
                          autoComplete="off"
                        />
                      </div>
                      
                      {/* Search Results */}
                      <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-md">
                        {searchResults.length > 0 ? (
                          searchResults.map((template) => (
                            <button
                              key={template.template_id}
                              type="button"
                              onClick={() => handleAddDocument(template.name)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 border-b border-slate-100 last:border-b-0"
                            >
                              <span>{template.name}</span>
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-sm text-slate-500 text-center">
                            {newDocumentName ? 'Δεν βρέθηκαν αποτελέσματα' : 'Πληκτρολογήστε για αναζήτηση'}
                          </div>
                        )}
                      </div>
                      
                      {/* Add custom document button */}
                      <div className="pt-2 border-t border-slate-200">
                        <button
                          type="button"
                          onClick={() => setShowOtherInput(true)}
                          className="w-full px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md flex items-center justify-center gap-2"
                        >
                          <Plus className="h-4 w-4" />
                          Άλλο Έγγραφο
                        </button>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleAddCustomDocument} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="custom-doc-name">Τίτλος Εγγράφου</Label>
                        <Input
                          id="custom-doc-name"
                          value={customDocName}
                          onChange={(e) => setCustomDocName(e.target.value)}
                          placeholder="π.χ. Υπεύθυνη Δήλωση"
                          required
                          autoFocus
                        />
                        <p className="text-xs text-slate-500">
                          Αυτό το έγγραφο δεν θα ελεγχθεί από AI καθώς δεν έχει καταχωρημένο prompt.
                        </p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setShowOtherInput(false);
                            setCustomDocName('');
                          }}
                          className="flex-1"
                        >
                          Πίσω
                        </Button>
                        <Button 
                          type="submit" 
                          className="flex-1 bg-slate-900 hover:bg-slate-800" 
                          disabled={loading}
                        >
                          {loading ? 'Προσθήκη...' : 'Προσθήκη'}
                        </Button>
                      </div>
                    </form>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Δικαιολογητικό
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Κατάσταση
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Αρχείο
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Υπογραφή
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Έξυπνος Έλεγχος
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Έλεγχος
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Ανάθεση
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Ενέργειες
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Ημ/νία Ανάρτησης
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200" data-testid="documents-table-body">
                  {documents.map((doc) => {
                    const filePaths = getFilePaths(doc);
                    const hasFiles = filePaths.length > 0;
                    const canAddMore = filePaths.length < 20;
                    
                    return (
                    <tr key={doc.document_id} className="hover:bg-slate-50" data-testid={`document-row-${doc.document_id}`}>
                      <td className="px-6 py-4">
                        <div className={`text-sm font-medium ${doc.needs_owner_review ? 'text-emerald-600' : 'text-slate-900'}`}>
                          {doc.document_name}
                          {doc.needs_owner_review && (
                            <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Νέο</span>
                          )}
                        </div>
                        {/* Show file count if multiple files */}
                        {filePaths.length > 1 && (
                          <span className="text-xs text-slate-500">({filePaths.length} αρχεία)</span>
                        )}
                        {/* Show note if exists */}
                        {doc.note && (
                          <div className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />
                            <span className="truncate max-w-[200px]">{doc.note}</span>
                          </div>
                        )}
                        {/* Expiry tracking */}
                        <div className="mt-1 flex items-center gap-1.5">
                          <button
                            onClick={() => openExpiryDialog(doc)}
                            className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded transition-colors ${
                              doc.expiry_date
                                ? getExpiryInfo(doc)?.expired
                                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                  : getExpiryInfo(doc)?.expiringSoon
                                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                    : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                            }`}
                            title="Ημερομηνίες Έκδοσης / Λήξης"
                            data-testid={`expiry-button-${doc.document_id}`}
                          >
                            <Clock className="h-3 w-3" />
                            {doc.expiry_date ? (
                              (() => {
                                const info = getExpiryInfo(doc);
                                if (info.expired) return `Έληξε (${Math.abs(info.daysLeft)} ημ.)`;
                                if (info.daysLeft === 0) return 'Λήγει σήμερα';
                                return `${info.daysLeft} ημ.`;
                              })()
                            ) : (
                              <span className="opacity-70">Λήξη</span>
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(doc)}</td>
                      <td className="px-6 py-4">
                        {hasFiles ? (
                          <div className="space-y-2">
                            {/* List of files */}
                            <div className="space-y-1">
                              {filePaths.map((fp, idx) => (
                                <div key={idx} className="flex items-center gap-1 text-xs bg-slate-50 px-2 py-1 rounded">
                                  <span className="truncate max-w-[120px]" title={getFileName(fp)}>
                                    {getFileName(fp)}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0 text-emerald-600 hover:text-emerald-700"
                                    onClick={() => handleDownload(doc.document_id, idx, getFileName(fp))}
                                  >
                                    <Download className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0 text-rose-600 hover:text-rose-700"
                                    onClick={() => handleDeleteFile(doc.document_id, idx)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                            
                            {/* Add more files button (if < 20) */}
                            {canAddMore && (
                              <div className="flex items-center gap-1">
                                <input
                                  type="file"
                                  id={`add-${doc.document_id}`}
                                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic,.heif,.webp,.tiff"
                                  multiple
                                  className="hidden"
                                  onChange={(e) => handleUpload(doc.document_id, e.target.files)}
                                />                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => document.getElementById(`add-${doc.document_id}`).click()}
                                  disabled={uploadingDocId === doc.document_id}
                                  className="text-blue-600 border-blue-300 hover:bg-blue-50 text-xs"
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  {uploadingDocId === doc.document_id ? '...' : `Προσθήκη (${5 - filePaths.length})`}
                                </Button>
                                
                                {/* Delete all files */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteFile(doc.document_id, -1)}
                                  className="text-rose-600 hover:text-rose-700 text-xs"
                                  title="Διαγραφή όλων"
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Όλα
                                </Button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              id={`file-${doc.document_id}`}
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic,.heif,.webp,.tiff"
                              multiple
                              className="hidden"
                              onChange={(e) => handleUpload(doc.document_id, e.target.files)}
                            />
                            <Button
                              className="bg-slate-900 hover:bg-slate-800"
                              size="sm"
                              onClick={() => document.getElementById(`file-${doc.document_id}`).click()}
                              disabled={uploadingDocId === doc.document_id}
                              data-testid={`upload-file-button-${doc.document_id}`}
                            >
                              <Upload className="h-3 w-3 mr-1" />
                              {uploadingDocId === doc.document_id ? 'Ανέβασμα...' : 'Ανέβασμα (έως 20)'}
                            </Button>
                          </div>
                        )}
                      </td>
                      {/* Signature Column */}
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          {/* Toggle Signature */}
                          <Button
                            variant={doc.signature_required ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleToggleSignature(doc)}
                            className={doc.signature_required ? "bg-amber-500 hover:bg-amber-600 text-white" : "text-slate-500"}
                            title={doc.signature_required ? "Απενεργοποίηση υπογραφής" : "Ενεργοποίηση υπογραφής"}
                          >
                            <FileSignature className="h-3 w-3 mr-1" />
                            {doc.signature_required ? "Ενεργή" : "Υπογραφή"}
                          </Button>
                          
                          {doc.signature_required && (
                            <div className="space-y-1">
                              {/* Upload file for signing */}
                              {!doc.signature_file_path && (
                                <div>
                                  <input
                                    type="file"
                                    id={`sign-file-${doc.document_id}`}
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic,.heif,.webp,.tiff"
                                    className="hidden"
                                    onChange={(e) => handleUploadForSigning(doc.document_id, e.target.files[0])}
                                  />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => document.getElementById(`sign-file-${doc.document_id}`).click()}
                                    disabled={signatureUploading === doc.document_id}
                                    className="text-xs w-full"
                                  >
                                    <Upload className="h-3 w-3 mr-1" />
                                    {signatureUploading === doc.document_id ? "..." : "Ανέβασμα για υπογραφή"}
                                  </Button>
                                </div>
                              )}
                              
                              {/* File for signing uploaded - show status */}
                              {doc.signature_file_path && (
                                <div className="text-xs space-y-1">
                                  <div className="flex items-center gap-1">
                                    <span className="text-amber-600">📄 Για υπογραφή</span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-5 w-5 p-0"
                                      onClick={() => handleDownloadForSigning(doc)}
                                      title="Λήψη"
                                    >
                                      <Download className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-5 w-5 p-0 text-rose-500"
                                      onClick={() => handleDeleteSignatureFile(doc.document_id, 'unsigned')}
                                      title="Διαγραφή"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  
                                  {/* Signed file status */}
                                  {doc.signature_status === 'signed' && doc.signed_file_path ? (
                                    <div className="flex items-center gap-1 text-emerald-600">
                                      <CheckCircle className="h-3 w-3" />
                                      <span>Υπογεγραμμένο</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-5 w-5 p-0"
                                        onClick={() => handleDownloadSigned(doc)}
                                        title="Λήψη υπογεγραμμένου"
                                      >
                                        <Download className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <span className="text-slate-400">Αναμονή υπογραφής...</span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {doc.ai_notes ? (
                          <div className="max-w-xs">
                            <p className="text-xs text-slate-400 mb-1">Αποτέλεσμα:</p>
                            <p className="text-sm text-slate-600">{doc.ai_notes}</p>
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {hasFiles ? (
                          <div className="flex items-center gap-1">
                            {doc.review_status === 'approved' ? (
                              <button
                                onClick={() => {
                                  setReviewingDocument(doc);
                                  setReviewData({ status: 'approved', comment: doc.review_comment || '' });
                                  setReviewDialogOpen(true);
                                }}
                                className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs hover:bg-emerald-100 cursor-pointer"
                              >
                                <Check className="h-3 w-3 mr-1" />Σωστό
                              </button>
                            ) : doc.review_status === 'rejected' ? (
                              <button
                                onClick={() => {
                                  setReviewingDocument(doc);
                                  setReviewData({ status: 'rejected', comment: doc.review_comment || '' });
                                  setReviewDialogOpen(true);
                                }}
                                className="text-left"
                              >
                                <span className="inline-flex items-center px-2 py-1 rounded-md bg-rose-50 text-rose-700 text-xs hover:bg-rose-100 cursor-pointer">
                                  <X className="h-3 w-3 mr-1" />Λάθος
                                </span>
                                {doc.review_comment && (
                                  <p className="text-xs text-rose-600 mt-1 max-w-[150px] truncate hover:underline" title={doc.review_comment}>
                                    {doc.review_comment}
                                  </p>
                                )}
                              </button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setReviewingDocument(doc);
                                  setReviewData({ status: 'approved', comment: '' });
                                  setReviewDialogOpen(true);
                                }}
                                className="text-xs"
                              >
                                <MessageSquare className="h-3 w-3 mr-1" />
                                Έλεγχος
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {collaborators.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {doc.assigned_to && doc.assigned_to.length > 0 ? (
                              <>
                                {collaborators
                                  .filter(c => doc.assigned_to?.includes(c.collaborator_id))
                                  .map(c => (
                                    <span key={c.collaborator_id} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                      {getRoleDisplayName(c.role, c.role_name)}
                                    </span>
                                  ))}
                                <button
                                  onClick={() => {
                                    setAssigningDocument(doc);
                                    setSelectedCollaborators(doc.assigned_to || []);
                                    setAssignDialogOpen(true);
                                  }}
                                  className="text-xs text-blue-600 hover:underline"
                                >
                                  Αλλαγή
                                </button>
                              </>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setAssigningDocument(doc);
                                  setSelectedCollaborators([]);
                                  setAssignDialogOpen(true);
                                }}
                                className="text-xs text-slate-500"
                              >
                                <Users className="h-3 w-3 mr-1" />
                                Ανάθεση
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">Προσθέστε συνεργάτες</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {/* Mark as reviewed button - only show if needs_owner_review */}
                        {doc.needs_owner_review && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 mr-1"
                            onClick={() => handleMarkReviewed(doc.document_id)}
                            title="Σημείωση ως ελεγμένο"
                            data-testid={`mark-reviewed-button-${doc.document_id}`}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                        
                        {/* Note button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`${doc.note ? 'text-blue-600 hover:text-blue-700' : 'text-slate-400 hover:text-slate-600'} hover:bg-slate-50`}
                          onClick={() => openNoteDialog(doc)}
                          title={doc.note ? 'Επεξεργασία σημείωσης' : 'Προσθήκη σημείωσης'}
                          data-testid={`note-document-button-${doc.document_id}`}
                        >
                          <MessageSquare className="h-4 w-4" />
                        </Button>

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              data-testid={`delete-document-button-${doc.document_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Διαγραφή Δικαιολογητικού</AlertDialogTitle>
                              <AlertDialogDescription>
                                Είστε σίγουροι ότι θέλετε να διαγράψετε το δικαιολογητικό &quot;{doc.document_name}&quot;;
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteDocument(doc.document_id)}
                                className="bg-rose-600 hover:bg-rose-700"
                              >
                                Διαγραφή
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </td>
                      {/* Upload Date Column */}
                      <td className="px-6 py-4">
                        {doc.uploaded_at ? (
                          <div className="text-sm">
                            <div className="text-slate-700">
                              {new Date(doc.uploaded_at).toLocaleDateString('el-GR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </div>
                            <div className="text-xs text-slate-400">
                              {new Date(doc.uploaded_at).toLocaleTimeString('el-GR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                            {doc.uploaded_by && (
                              <div className="text-xs text-slate-500 truncate max-w-[120px]" title={doc.uploaded_by}>
                                {doc.uploaded_by}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          </div>
          {/* Collaborators Section */}
          <div className="mt-8 bg-white border border-slate-200 rounded-md shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Users className="h-5 w-5" />
                Συνεργάτες ({collaborators.length})
              </h3>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendReminders}
                  disabled={sendingReminders || collaborators.length === 0}
                  className="text-amber-600 border-amber-600 hover:bg-amber-50"
                >
                  <Send className={`h-4 w-4 mr-2 ${sendingReminders ? 'animate-pulse' : ''}`} />
                  {sendingReminders ? 'Αποστολή...' : 'Αποστολή Ελλείψεων'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCollaboratorDialogOpen(true)}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Προσθήκη
                </Button>
              </div>
            </div>
            
            {collaborators.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {collaborators.map((collab) => (
                  <div 
                    key={collab.collaborator_id}
                    className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-md"
                  >
                    <div className="flex-1 min-w-0">
                      {collab.full_name && (
                        <span className="text-sm font-semibold mr-1">{collab.full_name}</span>
                      )}
                      <span className="text-sm text-slate-600">{collab.email}</span>
                      {collab.phone && (
                        <span className="text-xs text-slate-400 ml-1">({collab.phone})</span>
                      )}
                    </div>
                    <span className="text-xs text-slate-500 bg-white px-2 py-0.5 rounded shrink-0">
                      {getRoleDisplayName(collab.role, collab.role_name)}
                    </span>
                    <button
                      onClick={() => openEditCollaborator(collab)}
                      className="text-slate-400 hover:text-blue-600 shrink-0"
                      title="Επεξεργασία"
                      data-testid={`edit-collaborator-${collab.collaborator_id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleRemoveCollaborator(collab.collaborator_id)}
                      className="text-slate-400 hover:text-rose-600 shrink-0"
                      title="Αφαίρεση"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Δεν έχουν προστεθεί συνεργάτες</p>
            )}
          </div>
        </div>
      </div>

      {/* Add Collaborator Dialog */}
      <Dialog open={collaboratorDialogOpen} onOpenChange={setCollaboratorDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Προσθήκη Συνεργάτη</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCollaborator} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="collab-fullname">Ονοματεπώνυμο</Label>
              <Input
                id="collab-fullname"
                value={newCollaborator.full_name}
                onChange={(e) => setNewCollaborator({ ...newCollaborator, full_name: e.target.value })}
                placeholder="π.χ. Γιάννης Παπαδόπουλος"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="collab-email">Email</Label>
              <Input
                id="collab-email"
                type="email"
                value={newCollaborator.email}
                onChange={(e) => setNewCollaborator({ ...newCollaborator, email: e.target.value })}
                placeholder="email@example.com"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="collab-phone">Τηλέφωνο</Label>
              <Input
                id="collab-phone"
                type="tel"
                value={newCollaborator.phone}
                onChange={(e) => setNewCollaborator({ ...newCollaborator, phone: e.target.value })}
                placeholder="69xxxxxxxx"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Ρόλος</Label>
              <Select
                value={newCollaborator.role}
                onValueChange={(value) => setNewCollaborator({ ...newCollaborator, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Πελάτης</SelectItem>
                  <SelectItem value="accountant">Λογιστής</SelectItem>
                  <SelectItem value="other">Άλλος</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {newCollaborator.role === 'other' && (
              <div className="space-y-2">
                <Label htmlFor="role-name">Όνομα Ρόλου</Label>
                <Input
                  id="role-name"
                  value={newCollaborator.role_name}
                  onChange={(e) => setNewCollaborator({ ...newCollaborator, role_name: e.target.value })}
                  placeholder="π.χ. Δικηγόρος"
                />
              </div>
            )}
            
            <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading}>
              {loading ? 'Προσθήκη...' : 'Προσθήκη Συνεργάτη'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Collaborator Dialog */}
      <Dialog open={editCollaboratorDialogOpen} onOpenChange={setEditCollaboratorDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Επεξεργασία Συνεργάτη
            </DialogTitle>
          </DialogHeader>
          {editingCollaborator && (
            <form onSubmit={handleUpdateCollaborator} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-collab-fullname">Ονοματεπώνυμο</Label>
                <Input
                  id="edit-collab-fullname"
                  value={editingCollaborator.full_name || ''}
                  onChange={(e) => setEditingCollaborator({ ...editingCollaborator, full_name: e.target.value })}
                  placeholder="π.χ. Γιάννης Παπαδόπουλος"
                  data-testid="edit-collab-fullname"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-collab-email">Email</Label>
                <Input
                  id="edit-collab-email"
                  type="email"
                  value={editingCollaborator.email || ''}
                  onChange={(e) => setEditingCollaborator({ ...editingCollaborator, email: e.target.value })}
                  placeholder="email@example.com"
                  required
                  data-testid="edit-collab-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-collab-phone">Τηλέφωνο</Label>
                <Input
                  id="edit-collab-phone"
                  type="tel"
                  value={editingCollaborator.phone || ''}
                  onChange={(e) => setEditingCollaborator({ ...editingCollaborator, phone: e.target.value })}
                  placeholder="69xxxxxxxx"
                  data-testid="edit-collab-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Ρόλος</Label>
                <Select
                  value={editingCollaborator.role}
                  onValueChange={(value) => setEditingCollaborator({ ...editingCollaborator, role: value })}
                >
                  <SelectTrigger data-testid="edit-collab-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Πελάτης</SelectItem>
                    <SelectItem value="accountant">Λογιστής</SelectItem>
                    <SelectItem value="other">Άλλος</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editingCollaborator.role === 'other' && (
                <div className="space-y-2">
                  <Label htmlFor="edit-collab-role-name">Όνομα Ρόλου</Label>
                  <Input
                    id="edit-collab-role-name"
                    value={editingCollaborator.role_name || ''}
                    onChange={(e) => setEditingCollaborator({ ...editingCollaborator, role_name: e.target.value })}
                    placeholder="π.χ. Δικηγόρος"
                  />
                </div>
              )}
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid="save-edit-collaborator">
                {loading ? 'Αποθήκευση...' : 'Αποθήκευση Αλλαγών'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Document Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Έλεγχος Εγγράφου</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReviewDocument} className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-md">
              <p className="text-sm font-medium">{reviewingDocument?.document_name}</p>
            </div>
            
            <div className="space-y-2">
              <Label>Αποτέλεσμα Ελέγχου</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={reviewData.status === 'approved' ? 'default' : 'outline'}
                  className={reviewData.status === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700 flex-1' : 'flex-1'}
                  onClick={() => setReviewData({ ...reviewData, status: 'approved' })}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Σωστό
                </Button>
                <Button
                  type="button"
                  variant={reviewData.status === 'rejected' ? 'default' : 'outline'}
                  className={reviewData.status === 'rejected' ? 'bg-rose-600 hover:bg-rose-700 flex-1' : 'flex-1'}
                  onClick={() => setReviewData({ ...reviewData, status: 'rejected' })}
                >
                  <X className="h-4 w-4 mr-2" />
                  Λάθος
                </Button>
              </div>
            </div>
            
            {reviewData.status === 'rejected' && (
              <div className="space-y-2">
                <Label htmlFor="review-comment">Σχόλιο (τι πρέπει να διορθωθεί)</Label>
                <Textarea
                  id="review-comment"
                  value={reviewData.comment}
                  onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                  placeholder="Περιγράψτε το πρόβλημα..."
                  rows={3}
                />
              </div>
            )}
            
            <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading}>
              {loading ? 'Αποθήκευση...' : 'Αποθήκευση Ελέγχου'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Document Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ανάθεση Εγγράφου</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-md">
              <p className="text-sm font-medium">{assigningDocument?.document_name}</p>
            </div>
            
            <div className="space-y-2">
              <Label>Επιλέξτε Συνεργάτες</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {collaborators.map((collab) => (
                  <label 
                    key={collab.collaborator_id} 
                    className="flex items-center gap-3 p-2 border rounded-md hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCollaborators.includes(collab.collaborator_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCollaborators([...selectedCollaborators, collab.collaborator_id]);
                        } else {
                          setSelectedCollaborators(selectedCollaborators.filter(id => id !== collab.collaborator_id));
                        }
                      }}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <div>
                      <p className="text-sm font-medium">{collab.email}</p>
                      <p className="text-xs text-slate-500">{getRoleDisplayName(collab.role, collab.role_name)}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setAssignDialogOpen(false);
                  setAssigningDocument(null);
                  setSelectedCollaborators([]);
                }}
                className="flex-1"
              >
                Ακύρωση
              </Button>
              <Button
                onClick={handleAssignDocument}
                disabled={loading || selectedCollaborators.length === 0}
                className="flex-1 bg-slate-900 hover:bg-slate-800"
              >
                {loading ? 'Αποθήκευση...' : 'Αποθήκευση'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notification Settings Dialog */}
      <Dialog open={notificationDialogOpen} onOpenChange={setNotificationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Ρυθμίσεις Ειδοποιήσεων
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveNotificationSettings} className="space-y-6">
            <div className="space-y-4">
              {/* Info box about manual notifications */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">💡 Υπενθύμιση:</span> Οι υπενθυμίσεις προς τους συνεργάτες στέλνονται χειροκίνητα 
                  μέσω του <span className="font-semibold">Βοηθού Ειδοποιήσεων</span>.
                </p>
              </div>

              {/* Advisor Escalation Section - ONLY THIS REMAINS */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-slate-800">🚨 Ειδοποίηση Συμβούλου</h4>
                  <p className="text-sm text-slate-500">
                    Ειδοποίηση αν ο πελάτης δεν ανταποκρίνεται
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setNotificationSettings({
                    ...notificationSettings,
                    advisor_escalation_enabled: !notificationSettings.advisor_escalation_enabled
                  })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    notificationSettings.advisor_escalation_enabled ? 'bg-red-600' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      notificationSettings.advisor_escalation_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {notificationSettings.advisor_escalation_enabled && (
                <div className="space-y-2 bg-red-50 p-4 rounded-lg border border-red-200">
                  <Label htmlFor="escalation-threshold">Αριθμός Αναπάντητων Υπενθυμίσεων</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      id="escalation-threshold"
                      type="number"
                      min="1"
                      max="10"
                      value={notificationSettings.advisor_escalation_threshold}
                      onChange={(e) => setNotificationSettings({
                        ...notificationSettings,
                        advisor_escalation_threshold: parseInt(e.target.value) || 1
                      })}
                      className="w-24 bg-white"
                    />
                    <span className="text-slate-600">φορές</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-2">
                    📞 Αν ο πελάτης δεν ανταποκριθεί σε <strong>{notificationSettings.advisor_escalation_threshold}</strong> υπενθυμίσ{notificationSettings.advisor_escalation_threshold === 1 ? 'η' : 'εις'} 
                    (μετά από <strong>{notificationSettings.advisor_escalation_threshold * 24} ώρες</strong>), θα λάβετε email να τον καλέσετε τηλεφωνικά.
                  </p>
                  {project.unanswered_reminders_count > 0 && (
                    <p className="text-xs text-red-600 font-medium mt-2">
                      ⚠️ Τρέχουσες αναπάντητες υπενθυμίσεις: {project.unanswered_reminders_count}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setNotificationDialogOpen(false)}
                className="flex-1"
              >
                Ακύρωση
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-slate-900 hover:bg-slate-800"
                disabled={loading}
              >
                {loading ? 'Αποθήκευση...' : 'Αποθήκευση'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI Check Loading Dialog */}
      <Dialog open={aiCheckDialogOpen} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              Αυτόματος Έλεγχος Δικαιολογητικών
            </DialogTitle>
            <DialogDescription className="text-center pt-4">
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
                <p className="text-lg font-medium text-slate-900">
                  Παρακαλώ περιμένετε...
                </p>
                <p className="text-slate-600">
                  Πραγματοποιείται αυτόματος έλεγχος δικαιολογητικών.
                </p>
                <p className="text-sm text-slate-500">
                  Η διαδικασία μπορεί να διαρκέσει μερικά δευτερόλεπτα.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={editProjectDialogOpen} onOpenChange={setEditProjectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Επεξεργασία Έργου
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-project-name">Όνομα Έργου</Label>
              <Input
                id="edit-project-name"
                value={editProjectName}
                onChange={(e) => setEditProjectName(e.target.value)}
                placeholder="π.χ. Ψηφιακός Μετασχηματισμός ΜΜΕ"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-client-name">Όνομα Πελάτη</Label>
              <Input
                id="edit-client-name"
                value={editClientName}
                onChange={(e) => setEditClientName(e.target.value)}
                placeholder="π.χ. Εταιρεία ΑΕ"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-deadline">Προθεσμία</Label>
              <Input
                id="edit-deadline"
                type="date"
                value={editDeadline ? editDeadline.split('T')[0] : ''}
                onChange={(e) => setEditDeadline(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Πρότυπο Αίτησης</Label>
              {project?.template_id ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-md border border-slate-200">
                  <LinkIcon className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm text-slate-700">
                    Συνδεδεμένο: <strong>{availableTemplates.find(t => t.template_id === project.template_id)?.name || project.template_id}</strong>
                  </span>
                </div>
              ) : (
                <Select
                  value={editTemplateId || '_none'}
                  onValueChange={(value) => setEditTemplateId(value === '_none' ? '' : value)}
                >
                  <SelectTrigger data-testid="edit-template-select">
                    <SelectValue placeholder="Επιλέξτε πρότυπο..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Χωρίς πρότυπο</SelectItem>
                    {availableTemplates.map((t) => (
                      <SelectItem key={t.template_id} value={t.template_id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditProjectDialogOpen(false)}>
              Ακύρωση
            </Button>
            <Button onClick={handleEditProject} className="bg-slate-900 hover:bg-slate-800">
              Αποθήκευση
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Σημείωση - {editingNoteDoc?.document_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Προσθέστε σημείωση για το δικαιολογητικό..."
              rows={4}
            />
          </div>
          <DialogFooter className="mt-4 flex justify-between">
            <Button 
              variant="destructive" 
              onClick={handleDeleteNote}
              disabled={!editingNoteDoc?.note}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Διαγραφή
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>
                Ακύρωση
              </Button>
              <Button onClick={handleSaveNote} className="bg-slate-900 hover:bg-slate-800">
                Αποθήκευση
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expiry Tracking Dialog */}
      <Dialog open={expiryDialogOpen} onOpenChange={setExpiryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              Ημερομηνίες Ισχύος
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-md">
              <p className="text-sm font-medium">{expiryEditingDoc?.document_name}</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="expiry-issue-date">Ημερομηνία Έκδοσης</Label>
              <Input
                id="expiry-issue-date"
                type="date"
                value={expiryIssueDate}
                onChange={(e) => setExpiryIssueDate(e.target.value)}
                data-testid="expiry-issue-date-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="expiry-expiry-date">Ημερομηνία Λήξης</Label>
              <Input
                id="expiry-expiry-date"
                type="date"
                value={expiryExpiryDate}
                onChange={(e) => setExpiryExpiryDate(e.target.value)}
                data-testid="expiry-expiry-date-input"
              />
            </div>

            {expiryExpiryDate && (() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const expiry = new Date(expiryExpiryDate);
              expiry.setHours(0, 0, 0, 0);
              const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
              const expired = diffDays < 0;
              const expiringSoon = diffDays >= 0 && diffDays <= 30;
              return (
                <div className={`p-3 rounded-md text-sm ${
                  expired ? 'bg-red-50 text-red-700 border border-red-200' :
                  expiringSoon ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                  'bg-emerald-50 text-emerald-700 border border-emerald-200'
                }`}>
                  {expired ? (
                    <span>Το δικαιολογητικό έχει λήξει εδώ και <strong>{Math.abs(diffDays)}</strong> ημέρες</span>
                  ) : diffDays === 0 ? (
                    <span>Το δικαιολογητικό <strong>λήγει σήμερα</strong></span>
                  ) : (
                    <span>Απομένουν <strong>{diffDays}</strong> ημέρες μέχρι τη λήξη</span>
                  )}
                </div>
              );
            })()}
          </div>
          <DialogFooter className="mt-4 flex justify-between">
            <Button 
              variant="destructive" 
              onClick={handleClearExpiry}
              disabled={expirySaving || (!expiryEditingDoc?.expiry_date && !expiryEditingDoc?.issue_date)}
              size="sm"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Καθαρισμός
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setExpiryDialogOpen(false)}>
                Ακύρωση
              </Button>
              <Button 
                onClick={handleSaveExpiry} 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={expirySaving}
                data-testid="expiry-save-button"
              >
                {expirySaving ? 'Αποθήκευση...' : 'Αποθήκευση'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* R2 Relink Dialog */}
      <Dialog open={relinkDialogOpen} onOpenChange={setRelinkDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="r2-relink-dialog">
          <DialogHeader>
            <DialogTitle>
              {relinkStep === 'applied' ? 'Συγχρονισμός Ολοκληρώθηκε ✓' : 'Συγχρονισμός με Cloudflare R2'}
            </DialogTitle>
          </DialogHeader>
          
          {relinkLoading && !relinkResult ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-blue-600" />
              <p className="text-slate-600">Σαρώνω το R2 bucket...</p>
            </div>
          ) : relinkResult ? (
            <div className="space-y-4">
              {relinkResult.diagnostic && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">📊 Κατάσταση Αρχείων Project:</h4>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="bg-slate-50 border rounded-lg p-2 text-center">
                      <div className="text-xl font-bold text-slate-700">{relinkResult.diagnostic.summary.total_files}</div>
                      <div className="text-xs text-slate-600">Σύνολο</div>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
                      <div className="text-xl font-bold text-green-700">{relinkResult.diagnostic.summary.in_r2}</div>
                      <div className="text-xs text-slate-600">Στο R2</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 text-center">
                      <div className="text-xl font-bold text-purple-700">{relinkResult.diagnostic.summary.in_gridfs}</div>
                      <div className="text-xs text-slate-600">Στο GridFS</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-center">
                      <div className="text-xl font-bold text-red-700">{relinkResult.diagnostic.summary.lost_local}</div>
                      <div className="text-xs text-slate-600">Χαμένα</div>
                    </div>
                  </div>
                  {relinkResult.diagnostic.summary.r2_orphans_recoverable > 0 && (
                    <div className="mt-2 bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-800">
                      🔄 <strong>{relinkResult.diagnostic.summary.r2_orphans_recoverable}</strong> έγγραφα μπορούν να ανακτηθούν από το R2!
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700">{relinkResult.projects_scanned}</div>
                  <div className="text-xs text-slate-600">Projects</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-700">{relinkResult.documents_relinked}</div>
                  <div className="text-xs text-slate-600">{relinkStep === 'applied' ? 'Συνδέθηκαν' : 'Θα συνδεθούν'}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-slate-700">{relinkResult.documents_skipped}</div>
                  <div className="text-xs text-slate-600">Παραλήφθηκαν</div>
                </div>
              </div>
              {relinkResult.documents_relinked === 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800 font-medium mb-1">⚠️ Δεν βρέθηκαν αρχεία προς ανάκτηση</p>
                  <p className="text-xs text-amber-700">
                    Είτε όλα τα αρχεία είναι ήδη σωστά συνδεδεμένα, είτε δεν υπάρχουν αντίγραφα στο R2.
                  </p>
                </div>
              ) : relinkStep === 'applied' ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800 font-medium">
                    ✓ Επανήλθαν {relinkResult.documents_relinked} έγγραφα. Δοκιμάστε ξανά το κατέβασμα.
                  </p>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    Βρέθηκαν <strong>{relinkResult.documents_relinked}</strong> έγγραφα που μπορούν να συνδεθούν ξανά.
                    Πατήστε <strong>"Εφαρμογή"</strong>.
                  </p>
                </div>
              )}
              {relinkResult.details && relinkResult.details.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Λεπτομέρειες ({relinkResult.details.length}):</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-2 bg-slate-50">
                    {relinkResult.details.map((d, idx) => (
                      <div key={idx} className="text-xs bg-white border rounded p-2">
                        <div className="font-medium text-slate-900">{d.document_name || d.document_id}</div>
                        <div className="text-slate-500">
                          {d.collection === 'documents' ? 'Αίτηση' : d.collection === 'implementation_general_documents' ? 'Γενικά' : 'Παραστατικά'} •{' '}
                          {d.new_paths.length} αρχείο(α) στο R2
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <div className="flex justify-end gap-2 pt-4 border-t mt-4">
            {relinkStep === 'preview' && relinkResult?.documents_relinked > 0 && (
              <Button onClick={handleR2RelinkApply} disabled={relinkLoading} className="bg-green-600 hover:bg-green-700" data-testid="r2-relink-apply-btn">
                {relinkLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Εφαρμογή
              </Button>
            )}
            <Button variant="outline" onClick={() => setRelinkDialogOpen(false)} data-testid="r2-relink-close-btn">
              {relinkStep === 'applied' ? 'Κλείσιμο' : 'Άκυρο'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetail;
